import { useEffect } from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { Provider, useSetAtom } from 'jotai';
import { Outlet } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { MDSLNBItem, MDSLogo } from '@marqvision/mds-v2';
import { DownloadMonitoringPanel, useNavigationProps } from '@marqvision/shared';
import { checkIsClientView, useUserInfoQuery } from '~/apis/auth';
import { useGetProfileQuery } from '~/apis/settings';
import { userProfileAtom } from '~/atoms';
import { GlobalAlert } from '~/components/GlobalAlert';
import { LNB_STATE, LOG_EVENT_NAME } from '~/components/layout/AppLayout/@constants';
import { useLogger, useSentryUserInfoUpdate } from '~/hooks';
import { ClientViewHeader } from './ClientViewHeader';
import { GlobalNavigationBar } from './GlobalNavigationBar';
import { LeftNavigationBar } from './LeftNavigationBar';

const useStyles = makeStyles(({ color }) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: color.bg.surface.neutral.default.normal,
  },
  clientWrapper: {
    flex: 1,
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    gridTemplateColumns: 'auto 1fr',
    gridTemplateAreas: `
      "gnb gnb"
      "lnb main"
    `,
    overflow: 'hidden',
  },
  gnb: {
    gridArea: 'gnb',
  },
  main: {
    gridArea: 'main',
    overflow: 'hidden',
    position: 'relative',
  },
}));

export const AppLayout = () => {
  const classes = useStyles();
  const { amplitudeLogging } = useLogger();
  const growthBook = useGrowthBook();
  const { data: userData } = useUserInfoQuery();
  const clientId = userData?.client_id;

  const loggingLNBToggle = (isOpen: boolean) => {
    amplitudeLogging(LOG_EVENT_NAME.lnbToggle, {
      ['new_lnb_state']: isOpen ? LNB_STATE.open : LNB_STATE.folded,
    });
  };
  const loggingLNBItemClick = (item: Partial<MDSLNBItem>, isOpen: boolean) => {
    amplitudeLogging(LOG_EVENT_NAME.lnbTabClick, {
      ['tab_name']: item.label,
      ['parent_tab_name']: item.parentLabel,
      ['lnb_state']: isOpen ? LNB_STATE.open : LNB_STATE.folded,
    });
  };

  const isAdmin = checkIsClientView();
  const { lnbProps, gnbProps } = useNavigationProps({
    onLNBOpenChange: loggingLNBToggle,
    onLNBItemClick: loggingLNBItemClick,
    foldableFromPaths: ['/home'],
    persistence: {
      storageKey: 'marq-lnb-open-state',
    },
  });

  const setAtom = useSetAtom(userProfileAtom);

  const { data } = useGetProfileQuery({
    enabled: !!userData,
  });

  useSentryUserInfoUpdate();

  useEffect(() => {
    growthBook?.setAttributes({
      client_id: clientId,
    });
  }, [clientId, growthBook]);

  useEffect(() => {
    setAtom(data);
  }, [data, setAtom]);

  return (
    <div className={classes.wrapper}>
      {isAdmin && <ClientViewHeader logo={<MDSLogo color="white" logoType="vision" size={20} />} />}
      <div className={classes.clientWrapper}>
        <GlobalAlert />
        <div className={classes.gnb}>
          <GlobalNavigationBar {...gnbProps} />
        </div>
        <LeftNavigationBar {...lnbProps} />
        <main className={classes.main}>
          <Outlet />

          <Provider store={DownloadMonitoringPanel.jotaiStore}>
            <DownloadMonitoringPanel.Component />
          </Provider>
        </main>
      </div>
    </div>
  );
};
